import {
  JobScopeChange,
  JobScopeChangeItem,
  JobScopeChangeStatus as JobScopeChangeStatusProto,
  JobScopeItemType as JobScopeItemTypeProto,
  JobScopeResponderType as JobScopeResponderTypeProto,
  JobScopeChangePriceBreakdownForAdminResponse,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import User from 'iam/models/User'
import Workspace from 'iam/models/Workspace'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { Model } from 'infra/model'
import Job from 'jobs/Job'
import { PrimeJob } from 'jobs/JobStore'
import { collection } from 'infra/collection'
import once from 'lodash/once'
import { computed, makeObservable, observable } from 'mobx'
import { DateZero, maybeParseDate, timeago } from 'utils/date-util'
import { decimalToNumber, timestampToISO } from 'utils/grpc-util'
import { match } from 'utils/pattern-match-util'
import { isNotNil } from 'utils/object-util'

export enum ScopeChangeRequestStatus {
  PendingFinalization,
  PendingAdminApproval,
  PendingClientApproval,
  Accepted,
  Rejected,
  ApplyFailed,
  Unspecified,
}

export function toScopeChangeRequestStatus(
  status?: JobScopeChangeStatusProto,
): ScopeChangeRequestStatus {
  if (!status) {
    return ScopeChangeRequestStatus.PendingFinalization
  }

  switch (status) {
    case JobScopeChangeStatusProto.ACCEPTED:
      return ScopeChangeRequestStatus.Accepted
    case JobScopeChangeStatusProto.PENDING_ADMIN_APPROVAL:
      return ScopeChangeRequestStatus.PendingAdminApproval
    case JobScopeChangeStatusProto.PENDING_CLIENT_APPROVAL:
      return ScopeChangeRequestStatus.PendingClientApproval
    case JobScopeChangeStatusProto.REJECTED:
      return ScopeChangeRequestStatus.Rejected
    case JobScopeChangeStatusProto.APPLY_FAILED:
      return ScopeChangeRequestStatus.ApplyFailed
    case JobScopeChangeStatusProto.UNSPECIFIED:
      return ScopeChangeRequestStatus.Unspecified
  }
}

export enum ResponderType {
  Client,
  Admin,
  System,
}

export function toResponderType(
  type?: JobScopeResponderTypeProto,
): ResponderType {
  if (type == null) {
    return ResponderType.Admin
  }

  switch (type) {
    case JobScopeResponderTypeProto.CLIENT:
      return ResponderType.Client
    case JobScopeResponderTypeProto.ADMIN:
      return ResponderType.Admin
    case JobScopeResponderTypeProto.SYSTEM:
      return ResponderType.System
  }
}

export enum ResponseType {
  Approved,
  Rejected,
}
export function toResponseType(type: string): ResponseType {
  switch (type) {
    case 'Approved':
      return ResponseType.Approved
    case 'Rejected':
      return ResponseType.Rejected
    default:
      return ResponseType.Approved
  }
}

export class ScopeChangeRequestResponse {
  @observable createdByUser = User.none
  @observable responderType = ResponderType.Admin
  @observable responseType = ResponseType.Approved
  @observable reason?
  @observable dateCreated = DateZero()
  @observable job = Job.none

  constructor(
    createdByUserId: string,
    responderType: ResponderType,
    responseType: ResponseType,
    dateCreated: Date,
    job: Job,
    primeUser: PrimeUser,
    reason?: string,
  ) {
    makeObservable(this)
    this.createdByUser = primeUser({ public_id: createdByUserId })
    this.responderType = responderType
    this.responseType = responseType
    this.reason = reason
    this.dateCreated = dateCreated
    this.job = job
  }

  get responderUser() {
    switch (this.responderType) {
      case ResponderType.Admin:
        return 'Admin'
      case ResponderType.System:
        return 'System'
      case ResponderType.Client:
        return 'Client'
      default:
        return 'Client'
    }
  }

  get isAdmin() {
    return this.createdByUser.platformAdmin
  }

  @computed
  get collaborator() {
    return this.job.collaborators.find(
      (c) => c.member.user.publicId === this.createdByUser.publicId,
    )
  }
}

export enum ScopeChangeRequestItemType {
  Inventory,
  Custom = 1,
}

export function toScopeChangeRequestItemType(
  status: JobScopeItemTypeProto,
): ScopeChangeRequestItemType {
  switch (status) {
    case JobScopeItemTypeProto.INVENTORY:
      return ScopeChangeRequestItemType.Inventory
    case JobScopeItemTypeProto.CUSTOM:
      return ScopeChangeRequestItemType.Custom
  }
}

export class ScopeChangeRequestItem extends Model<
  JobScopeChangeItem.AsObject,
  ScopeChangeRequestItem
> {
  @observable id = ''
  @observable name = ''
  @observable inventoryItemVersionId = ''
  @observable quantity = 0
  @observable price = 0
  @observable proPrice = 0
  @observable type: ScopeChangeRequestItemType =
    ScopeChangeRequestItemType.Custom

  constructor() {
    super()
    makeObservable(this)
  }

  parse(json: JobScopeChangeItem.AsObject): Partial<ScopeChangeRequestItem> {
    return {
      id: json.id,
      name: json.name,
      quantity: json.quantity,
      price: json.price ? decimalToNumber(json.price) : 0,
      proPrice: json.proPrice ? decimalToNumber(json.proPrice) : 0,
      type: toScopeChangeRequestItemType(json.type),
      inventoryItemVersionId: json.inventoryItemVersionId?.value,
    }
  }
}

export default class ScopeChangeRequest extends Model<
  JobScopeChange.AsObject,
  ScopeChangeRequest
> {
  @observable id = ''
  @observable createTime = DateZero()
  @observable job = Job.none
  @observable createdBy = User.none
  @observable status: ScopeChangeRequestStatus =
    ScopeChangeRequestStatus.PendingAdminApproval

  @observable requireClientApproval = false

  @observable workspace = Workspace.none
  @observable responders: ScopeChangeRequestResponse[] = []
  @observable reason = ''

  items = collection<JobScopeChangeItem.AsObject, ScopeChangeRequestItem>({
    create: () => new ScopeChangeRequestItem(),
  })

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeJob: PrimeJob,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get isCreatedByAdmin() {
    return (
      this.job.collaborators.find(
        (c) => c.member.user.publicId === this.createdBy.publicId,
      ) === undefined
    )
  }

  @computed
  get customItems() {
    return this.items.filter(
      (i) => i.type === ScopeChangeRequestItemType.Custom,
    )
  }

  /**
   * Whether the request has custom items or not
   */
  @computed
  get hasCustomItems() {
    return this.customItems.length > 0
  }

  @computed
  get inventoryItems() {
    return this.items.filter(
      (i) => i.type === ScopeChangeRequestItemType.Inventory,
    )
  }

  /**
   * Whether the request has inventory items or not
   */
  @computed
  get hasInventoryItems() {
    return this.inventoryItems.length > 0
  }

  @computed
  get open() {
    return timeago(this.createTime, 'long')
  }

  @computed
  get isPendingFinalization() {
    return this.status === ScopeChangeRequestStatus.PendingFinalization
  }

  @computed
  get isPendingAdminApproval() {
    return this.status === ScopeChangeRequestStatus.PendingAdminApproval
  }

  @computed
  get isPendingClientApproval() {
    return this.status === ScopeChangeRequestStatus.PendingClientApproval
  }

  @computed
  get isAccepted() {
    return this.status === ScopeChangeRequestStatus.Accepted
  }

  @computed
  get isRejected() {
    return this.status === ScopeChangeRequestStatus.Rejected
  }

  @computed
  get price() {
    return this.items
      .map((i) => i.price)
      .reduce((total, price) => total + price, 0)
  }

  /**
   * Whether or not the scope change request is pending
   */
  @computed
  get isPending() {
    return [
      ScopeChangeRequestStatus.PendingClientApproval,
      ScopeChangeRequestStatus.PendingAdminApproval,
    ].includes(this.status)
  }

  /**
   * Whether or not the scope change request is resolved
   */
  @computed
  get isResolved() {
    return [
      ScopeChangeRequestStatus.Accepted,
      ScopeChangeRequestStatus.Rejected,
    ].includes(this.status)
  }

  @computed
  get jobClient() {
    return this.job.collaborators.find((c) => c.type === 'CLIENT')
  }

  /**
   * Whether the request has responders or not
   */
  @computed
  get hasResponders() {
    return this.responders.length > 0
  }

  @computed
  get lastResponder() {
    // Responders are ordered most-recent-first by the backend, so take the first entry.
    return this.responders[0]
  }

  /**
   * The collaborator user type of the member which created the job scope change request.
   */
  @computed
  get reasonCollaboratorType() {
    const reasonCollaborator = this.job.collaborators.find(
      (c) => c.member.user.publicId === this.createdBy.publicId,
    )

    // If collaborator is not on the job, it was an admin.
    if (reasonCollaborator === undefined) {
      return 'Admin'
    }

    return reasonCollaborator.type === 'CLIENT' ? 'Client' : 'Provider'
  }

  static get none() {
    return lazyNone()
  }

  parse(json: JobScopeChange.AsObject): Partial<ScopeChangeRequest> {
    const job = this.primeJob({ id: json.jobId }) || Job.none

    this.items.reset(json.itemsList)

    const createTime =
      maybeParseDate(timestampToISO(json.createTime)) || DateZero()

    const responders =
      json.respondersList
        // Backend returns responders ordered most-recent-first.
        ?.map(
          (r) =>
            new ScopeChangeRequestResponse(
              r.responderUserPublicId,
              toResponderType(r.responderType),
              toResponseType(r.responseType),
              maybeParseDate(timestampToISO(r.createTime)) || DateZero(),
              job,
              this.primeUser,
              r.reason?.value,
            ),
        ) || []

    return {
      id: json.id,
      createTime,
      job,
      createdBy: json.createdByUserPublicId
        ? this.primeUser({ public_id: json.createdByUserPublicId })
        : User.none,
      status: toScopeChangeRequestStatus(json.status),
      workspace: json.workspaceId
        ? this.primeWorkspace({ id: json.workspaceId })
        : Workspace.none,
      requireClientApproval: json.requireClientApproval,
      responders,
      reason: json.reason,
    }
  }
}

export class ScopeChangeRequestPriceBreakDown extends Model<
  JobScopeChangePriceBreakdownForAdminResponse.AsObject,
  ScopeChangeRequestPriceBreakDown
> {
  @observable previousTotalPrice = 0
  @observable newTotalPrice = 0
  @observable previousTotalProPrice = 0
  @observable newTotalProPrice = 0
  @observable status: ScopeChangeRequestStatus =
    ScopeChangeRequestStatus.PendingAdminApproval

  @observable items: ScopeChangePriceBreakDownItemForAdmin[] = []

  constructor() {
    super()
    makeObservable(this)
  }

  /**
   * Default value
   */
  static get none() {
    return lazyNonePriceBreakDown()
  }

  @computed
  get totalPriceDiff() {
    return this.newTotalPrice - this.previousTotalPrice
  }

  @computed
  get totalProPriceDiff() {
    return this.newTotalProPrice - this.previousTotalProPrice
  }

  @computed
  get percentageInventoryScopeChangeItems() {
    return this.items.filter(
      (i) =>
        i.priceType === JobScopeChangeItemPriceType.MARKDOWN ||
        i.priceType === JobScopeChangeItemPriceType.MARKUP,
    )
  }

  @computed
  get flatInventoryScopeChangeItems() {
    return this.items.filter(
      (i) => i.priceType === JobScopeChangeItemPriceType.FLAT && !i.bundleId,
    )
  }

  @computed
  get customScopeChangeItems() {
    return this.items.filter(
      (i) => i.priceType === JobScopeChangeItemPriceType.CUSTOM,
    )
  }

  @computed
  get bundleScopeChangeItems() {
    return this.items.filter((i) => isNotNil(i.bundleId))
  }

  parse(
    json: JobScopeChangePriceBreakdownForAdminResponse.AsObject,
  ): Partial<ScopeChangeRequestPriceBreakDown> {
    const items =
      json.itemsList?.map(
        (i) => new ScopeChangePriceBreakDownItemForAdmin(i),
      ) || []

    return {
      status: toScopeChangeRequestStatus(json.status),
      previousTotalPrice: json.previousTotalPrice
        ? decimalToNumber(json.previousTotalPrice)
        : 0,
      newTotalPrice: json.newTotalPrice
        ? decimalToNumber(json.newTotalPrice)
        : 0,
      previousTotalProPrice: json.previousTotalProPrice
        ? decimalToNumber(json.previousTotalProPrice)
        : 0,
      newTotalProPrice: json.newTotalProPrice
        ? decimalToNumber(json.newTotalProPrice)
        : 0,
      items,
    }
  }
}

export class ScopeChangePriceBreakDownItemForAdmin {
  @observable description: string = ''
  @observable inventoryItemVersionId: string | undefined = ''
  @observable name: string = ''
  @observable newPrice: number = 0
  @observable newProPrice: number = 0
  @observable newQuantity: number = 0
  @observable previousPrice: number = 0
  @observable previousProPrice: number = 0
  @observable previousQuantity: number = 0
  @observable priceType: JobScopeChangeItemPriceType =
    JobScopeChangeItemPriceType.FLAT

  @observable bundleId: string | null = null

  constructor(
    json: JobScopeChangePriceBreakdownForAdminResponse.JobScopeChangeItemBreakdownForAdmin.AsObject,
  ) {
    this.description = json.description
    this.inventoryItemVersionId = json.inventoryItemVersionId?.value
    this.name = json.name
    this.newPrice = json.newPrice ? decimalToNumber(json.newPrice) : 0
    this.newProPrice = json.newProPrice ? decimalToNumber(json.newProPrice) : 0
    this.previousPrice = json.previousPrice
      ? decimalToNumber(json.previousPrice)
      : 0
    this.previousProPrice = json.previousProPrice
      ? decimalToNumber(json.previousProPrice)
      : 0
    this.newQuantity = json.newQuantity
    this.previousQuantity = json.previousQuantity
    this.priceType = mapJobScopeChangePriceTypeToDTO(json.priceType)
    this.bundleId = isNotNil(json.bundleId) ? json.bundleId.value : null
    makeObservable(this)
  }

  @computed
  get priceDiff() {
    return this.newPrice - this.previousPrice
  }

  @computed
  get proPriceDiff() {
    return this.newProPrice - this.previousProPrice
  }

  @computed
  get quantityDiff() {
    return this.newQuantity - this.previousQuantity
  }
}

const lazyNone = once(
  () =>
    new ScopeChangeRequest(
      () => Workspace.none,
      () => User.none,
      () => Job.none,
    ),
)

const lazyNonePriceBreakDown = once(
  () => new ScopeChangeRequestPriceBreakDown(),
)

function mapJobScopeChangePriceTypeToDTO(
  priceType: JobScopeChangePriceBreakdownForAdminResponse.JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType,
): JobScopeChangeItemPriceType {
  return match(priceType, {
    [JobScopeChangePriceBreakdownForAdminResponse
      .JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType.FEE]:
      () => JobScopeChangeItemPriceType.FEE,
    [JobScopeChangePriceBreakdownForAdminResponse
      .JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType.FLAT]:
      () => JobScopeChangeItemPriceType.FLAT,
    [JobScopeChangePriceBreakdownForAdminResponse
      .JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType.MARKUP]:
      () => JobScopeChangeItemPriceType.MARKUP,
    [JobScopeChangePriceBreakdownForAdminResponse
      .JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType
      .MARKDOWN]: () => JobScopeChangeItemPriceType.MARKDOWN,
    [JobScopeChangePriceBreakdownForAdminResponse
      .JobScopeChangeItemBreakdownForAdmin.JobScopeChangeItemPriceType.CUSTOM]:
      () => JobScopeChangeItemPriceType.CUSTOM,
  })
}

export enum JobScopeChangeItemPriceType {
  FLAT = 0,
  FEE = 1,
  MARKUP = 2,
  MARKDOWN = 3,
  CUSTOM = 4,
}
